import { IMedia } from '@dtos/Course'
import { useCallback, useMemo } from 'react'
import { useWindowDimensions } from 'react-native'

const VIDEO_PLAYER_HEIGHT = {
  xs: 214,
  sm: 352,
  md: 352,
  lg: 446,
  xl: 446,
  xxl: 446,
}

const VIDEO_PLAYER_HEIGHT_EXTRA_CONTENT = {
  ...VIDEO_PLAYER_HEIGHT,
  xs: 218,
}

const TEXT_PLAYER_HEIGHT = {
  xs: 510,
  sm: 636,
  md: 606,
  lg: 606,
  xl: 606,
  xxl: 606,
}

const TEXT_PLAYER_HEIGHT_LOW_HEIGHT = {
  xs: 185,
  sm: 280,
  md: 352,
  lg: 446,
  xl: 446,
  xxl: 446,
}

const IMAGE_PLAYER_HEIGHT = {
  xs: 185,
  sm: 280,
  md: 352,
  lg: 446,
  xl: 446,
  xxl: 446,
}

const EXPANDED_IMAGE_PLAYER_HEIGHT = {
  xs: 600,
  sm: 600,
  md: 650,
  lg: 750,
  xl: 750,
  xxl: 750,
}

const DOCUMENT_PLAYER_HEIGHT = {
  xs: 185,
  sm: 550,
  md: 650,
  lg: 750,
  xl: 850,
  xxl: 950,
}

const SPREADSHEET_PLAYER_HEIGHT = {
  xs: 185,
  sm: 185,
  md: 185,
  lg: 185,
  xl: 185,
  xxl: 185,
}

const AUDIO_PLAYER_HEIGHT = {
  xs: 184,
  sm: 184,
  md: 184,
  lg: 184,
  xl: 184,
  xxl: 184,
}

interface IMediaRenderWrapperDynamicHeightProps {
  mediaSize: string
  mediaType: IMedia['type']
  isExpanded: boolean
  textHeight: number
  location?: 'extraContent' | 'courseContent'
  fileExtension: string | null
}

export function useMediaRenderWrapperDynamicHeight({
  mediaSize,
  mediaType,
  isExpanded,
  textHeight,
  location,
  fileExtension,
}: IMediaRenderWrapperDynamicHeightProps) {
  const { height: windowHeight } = useWindowDimensions()

  const getDynamicHeight = useCallback(
    (baseHeight: number) => {
      if (['xs', 'sm'].includes(mediaSize)) {
        return windowHeight - 120
      }
      return baseHeight
    },
    [mediaSize, windowHeight],
  )

  const getTextHeight = useMemo(() => {
    if (mediaType !== 'Text') return 0
    if (textHeight < 185) {
      return TEXT_PLAYER_HEIGHT_LOW_HEIGHT[
        mediaSize as keyof typeof TEXT_PLAYER_HEIGHT_LOW_HEIGHT
      ]
    }

    if (textHeight >= 185 && textHeight < 510) {
      return textHeight + 16
    }

    return TEXT_PLAYER_HEIGHT[mediaSize as keyof typeof TEXT_PLAYER_HEIGHT]
  }, [textHeight, mediaSize, mediaType])

  const isSpredSheet = useMemo(() => {
    if (!fileExtension) return false
    return ['.xls', '.xlsm', '.xlsx', '.csv'].includes(fileExtension)
  }, [fileExtension])

  const height = useMemo(() => {
    let height: number

    switch (mediaType) {
      case 'Video':
        height =
          location === 'extraContent'
            ? VIDEO_PLAYER_HEIGHT_EXTRA_CONTENT[
                mediaSize as keyof typeof VIDEO_PLAYER_HEIGHT_EXTRA_CONTENT
              ]
            : VIDEO_PLAYER_HEIGHT[mediaSize as keyof typeof VIDEO_PLAYER_HEIGHT]
        break
      case 'Image':
        height = isExpanded
          ? getDynamicHeight(
              EXPANDED_IMAGE_PLAYER_HEIGHT[
                mediaSize as keyof typeof EXPANDED_IMAGE_PLAYER_HEIGHT
              ],
            )
          : IMAGE_PLAYER_HEIGHT[mediaSize as keyof typeof IMAGE_PLAYER_HEIGHT]
        break
      case 'Text':
        height = getTextHeight
        break
      case 'Document':
        height = isSpredSheet
          ? SPREADSHEET_PLAYER_HEIGHT[
              mediaSize as keyof typeof SPREADSHEET_PLAYER_HEIGHT
            ]
          : DOCUMENT_PLAYER_HEIGHT[
              mediaSize as keyof typeof DOCUMENT_PLAYER_HEIGHT
            ]
        break
      case 'Audio':
        height =
          AUDIO_PLAYER_HEIGHT[mediaSize as keyof typeof AUDIO_PLAYER_HEIGHT]
        break
      default:
        height =
          VIDEO_PLAYER_HEIGHT[mediaSize as keyof typeof VIDEO_PLAYER_HEIGHT]
    }

    return height
  }, [
    mediaType,
    mediaSize,
    isExpanded,
    getTextHeight,
    location,
    getDynamicHeight,
    isSpredSheet,
  ])

  return height
}
